import React from 'react';
import { LogOut, Moon, PanelRightClose, PanelRightOpen, Settings, Sun, Zap } from 'lucide-react';
import { UserRole } from '@repo/shared-types';
import type { NavItem } from '@repo/shared-config';
import { Z_INDEX } from '@/utils/zIndex';
import { LoadingSpinner } from '../ui/LoadingSpinner';

export interface NavigationSidebarUser {
  fullName: string;
  avatar?: string | null;
}

export interface NavigationSidebarProps {
  currentNav: NavItem[];
  currentNavId: string;
  onNavigate: (view: string) => void;
  loadingNavId: string | null;
  userRole: UserRole;
  onRoleChange: (role: UserRole) => void;
  theme: string;
  onToggleTheme: () => void;
  /** Controlled: detail panel collapsed (rail only) when true. Owned by Layout. */
  sidebarDetailCollapsed: boolean;
  /** Toggle detail panel collapse; Layout persists to localStorage. */
  onSidebarDetailCollapseToggle: () => void;
  /** Controlled: primary rail expanded to show labels (Accounting, Operations, etc.). Owned by Layout. */
  sidebarRailExpanded: boolean;
  /** Toggle primary rail expanded (show labels) vs icon-only; Layout persists to localStorage. */
  onSidebarRailExpandToggle: () => void;
  currentUser: NavigationSidebarUser | null | undefined;
  loadingUser: boolean;
  onSettingsClick: () => void;
  /** Called when user chooses Sign out; clears session and redirects to login */
  onSignOut?: () => void;
  /** Optional class for mobile show/hide (e.g. translate for drawer) */
  className?: string;
  isMobileOpen?: boolean;
  /** Count badges keyed by nav leaf `id` (e.g. maintenance work orders). */
  navLeafBadgeCounts?: Record<string, number>;
}

export const NavigationSidebar: React.FC<NavigationSidebarProps> = ({
  currentNav,
  currentNavId,
  onNavigate,
  loadingNavId,
  userRole,
  onRoleChange: _onRoleChange,
  theme,
  onToggleTheme,
  sidebarDetailCollapsed,
  onSidebarDetailCollapseToggle,
  sidebarRailExpanded,
  onSidebarRailExpandToggle,
  currentUser,
  loadingUser,
  onSettingsClick,
  onSignOut,
  className = '',
  isMobileOpen: _isMobileOpen = false,
  navLeafBadgeCounts,
}) => {
  const [activeSectionId, setActiveSectionId] = React.useState<string>(currentNav[0]?.id ?? 'dashboard');
  const [hoveredSectionId, setHoveredSectionId] = React.useState<string | null>(null);
  const hoverOpenTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverCloseTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const HOVER_OPEN_DELAY_MS = 200;
  const HOVER_CLOSE_DELAY_MS = 100;

  React.useEffect(() => {
    const matchedSection =
      currentNav.find((section) => section.id === currentNavId) ??
      currentNav.find((section) =>
        section.children?.some((child) => child.id === currentNavId),
      );
    if (matchedSection) {
      setActiveSectionId(matchedSection.id);
    }
  }, [currentNav, currentNavId]);

  const activeSection = currentNav.find((section) => section.id === activeSectionId) ?? currentNav[0];
  const detailItems = activeSection?.children ?? [];

  const handleSectionClick = (section: NavItem) => {
    setActiveSectionId(section.id);

    const children = section.children ?? [];

    // If 0 children, navigate to section ID
    if (children.length === 0) {
      onNavigate(section.id);
    }
    // If 1 or more children, always navigate to the first child's ID
    // This provides a "one-click" experience for complex sections (Portfolio, Accounting)
    // while still opening the secondary rail for sub-navigation.
    else {
      onNavigate(children[0].id);
    }
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 bg-slate-900 text-white flex transition-transform duration-300 transform md:relative md:translate-x-0 pointer-events-auto ${className}`.trim()}
    >
      <div
        className={`border-r border-slate-800 flex flex-col py-3 bg-slate-950/40 transition-[width] duration-300 ease-out flex-shrink-0 ${
          sidebarRailExpanded ? 'w-56 px-3' : 'w-16 items-center px-2'
        }`}
      >
        <div className={`flex items-center mb-3 ${sidebarRailExpanded ? 'gap-3' : 'justify-center'}`}>
          <div className="w-10 h-10 bg-brand-600 rounded-lg flex items-center justify-center shadow-lg shadow-brand-500/20 shrink-0">
            <Zap className="text-white" size={20} fill="currentColor" />
          </div>
          {sidebarRailExpanded && (
            <span className="font-semibold text-white truncate">Menu</span>
          )}
        </div>

        <nav className="flex-1 w-full space-y-1 overflow-y-auto custom-scrollbar">
          {currentNav.map((section) => {
            const sectionActive =
              section.id === activeSectionId ||
              section.id === currentNavId ||
              (section.children?.some((child) => child.id === currentNavId) ?? false);
            const children = section.children ?? [];
            const hasChildren = children.length > 0;
            const isHovered = hoveredSectionId === section.id;

            const clearOpenTimer = () => {
              if (hoverOpenTimerRef.current) {
                clearTimeout(hoverOpenTimerRef.current);
                hoverOpenTimerRef.current = null;
              }
            };
            const clearCloseTimer = () => {
              if (hoverCloseTimerRef.current) {
                clearTimeout(hoverCloseTimerRef.current);
                hoverCloseTimerRef.current = null;
              }
            };

            if (sidebarRailExpanded) {
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => handleSectionClick(section)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition ${
                    sectionActive
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <section.icon size={18} className="shrink-0" />
                  <span className="font-medium truncate">{section.label}</span>
                  {navLeafBadgeCounts &&
                    (section.children ?? []).some((c) => (navLeafBadgeCounts[c.id] ?? 0) > 0) && (
                      <span className="ml-auto min-w-[1.25rem] rounded-full bg-amber-500 px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-white">
                        {(section.children ?? []).reduce((sum, c) => sum + (navLeafBadgeCounts[c.id] ?? 0), 0)}
                      </span>
                    )}
                </button>
              );
            }

            return (
              <div
                key={section.id}
                className="relative flex justify-center"
                onMouseEnter={() => {
                  clearCloseTimer();
                  if (!hasChildren) return;
                  hoverOpenTimerRef.current = setTimeout(() => setHoveredSectionId(section.id), HOVER_OPEN_DELAY_MS);
                }}
                onMouseLeave={() => {
                  clearOpenTimer();
                  hoverCloseTimerRef.current = setTimeout(() => setHoveredSectionId(null), HOVER_CLOSE_DELAY_MS);
                }}
              >
                <button
                  type="button"
                  onClick={() => handleSectionClick(section)}
                  title={section.label}
                  aria-haspopup={hasChildren ? 'true' : undefined}
                  aria-expanded={hasChildren && isHovered}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition ${sectionActive
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }`}
                >
                  <section.icon size={18} />
                </button>
                {hasChildren && isHovered && (
                  <div
                    role="menu"
                    className={`absolute left-full top-0 ml-1 min-w-[11rem] py-1 rounded-lg border border-slate-700 bg-slate-800 shadow-xl ${Z_INDEX.popover} hidden md:block`}
                    onMouseEnter={clearCloseTimer}
                  >
                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-700">
                      {section.label}
                    </div>
                    {children.map((child) => {
                      const isActive = currentNavId === child.id;
                      return (
                        <button
                          key={child.id}
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            onNavigate(child.id);
                            setHoveredSectionId(null);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm transition ${isActive
                            ? 'bg-slate-700 text-white font-medium'
                            : 'text-slate-300 hover:text-white hover:bg-slate-700/70'
                            }`}
                        >
                          {child.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <button
          type="button"
          onClick={onSidebarRailExpandToggle}
          aria-expanded={sidebarRailExpanded}
          aria-label={sidebarRailExpanded ? 'Collapse to icons only' : 'Expand to show labels'}
          title={sidebarRailExpanded ? 'Collapse to icons only' : 'Expand to show labels'}
          className={`mt-2 mb-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition ${sidebarRailExpanded ? 'flex items-center gap-2 w-full px-3 py-2 text-left' : 'p-2'}`}
        >
          {sidebarRailExpanded ? <PanelRightClose size={16} aria-hidden /> : <PanelRightOpen size={16} aria-hidden />}
          {sidebarRailExpanded && <span className="text-xs font-medium">Collapse</span>}
        </button>

        <button
          onClick={onToggleTheme}
          title="Toggle Theme"
          className={`rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition ${sidebarRailExpanded ? 'flex items-center gap-2 w-full px-3 py-2 mb-1 text-left' : 'mb-1 p-2'}`}
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          {sidebarRailExpanded && <span className="text-xs font-medium">Theme</span>}
        </button>

        <button
          type="button"
          onClick={onSettingsClick}
          className="w-10 h-10 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition mb-2"
          title="Settings"
        >
          <Settings size={18} />
        </button>

        {currentUser?.avatar ? (
          <img
            src={currentUser.avatar}
            alt={currentUser.fullName}
            className="w-8 h-8 rounded-full border border-slate-600"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-600" />
        )}
      </div>

      <div
        className={`flex flex-col flex-shrink-0 border-r border-slate-800 bg-slate-900 transition-[width] duration-300 ease-out ${sidebarDetailCollapsed ? 'w-[2rem] overflow-hidden' : 'w-72'}`}
        style={sidebarDetailCollapsed ? { transformOrigin: 'top right' } : undefined}
      >
        {sidebarDetailCollapsed ? (
          <button
            type="button"
            onClick={onSidebarDetailCollapseToggle}
            aria-expanded="false"
            aria-label="Expand sidebar"
            className="mt-3 flex h-10 w-8 items-center justify-center rounded-l-lg bg-slate-800/80 text-slate-400 transition hover:bg-slate-700 hover:text-white"
            title="Expand section panel"
          >
            <PanelRightOpen size={18} aria-hidden />
          </button>
        ) : (
          <>
            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-800 p-4">
                <div className="min-w-0">
                  <div className="text-sm text-slate-400">Section</div>
                  <div className="truncate font-semibold text-white">{activeSection?.label ?? 'Navigation'}</div>
                </div>
                <button
                  type="button"
                  onClick={onSidebarDetailCollapseToggle}
                  aria-expanded="true"
                  aria-label="Collapse sidebar"
                  className="rounded-lg bg-slate-800 p-1.5 text-slate-400 transition hover:bg-slate-700 hover:text-white"
                  title="Collapse section panel"
                >
                  <PanelRightClose size={16} aria-hidden />
                </button>
              </div>
        <nav className="flex-1 p-3 overflow-y-auto custom-scrollbar">
          {activeSection?.category ? (
            <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {activeSection.category}
            </p>
          ) : null}
          {detailItems.length > 0 ? (
            detailItems.map((child) => {
              const isLoading = loadingNavId === child.id;
              const isActive = currentNavId === child.id;
              return (
                <button
                  key={child.id}
                  type="button"
                  onClick={() => onNavigate(child.id)}
                  className={`w-full flex items-center justify-start px-3 py-2 rounded-md text-sm transition-all active:scale-[0.98] text-left mb-1 ${isActive
                    ? 'bg-slate-800 text-white font-medium shadow-inner'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                    }`}
                >
                  <span className="truncate flex-1 text-left" title={child.label}>
                    {child.label}
                  </span>
                  {navLeafBadgeCounts && (navLeafBadgeCounts[child.id] ?? 0) > 0 ? (
                    <span className="ml-2 shrink-0 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold leading-none text-white">
                      {navLeafBadgeCounts[child.id]}
                    </span>
                  ) : null}
                  {isLoading && <LoadingSpinner size="sm" className="text-cyan-400 ml-2" />}
                </button>
              );
            })
          ) : (
            <p className="px-3 py-2 text-sm text-slate-500">No items in this section.</p>
          )}
        </nav>

        <div className="p-4 border-t border-slate-800 bg-slate-900">
          <div
            onClick={onSettingsClick}
            className="flex items-center space-x-3 p-2 rounded-lg cursor-pointer hover:bg-slate-800 transition"
          >
            {loadingUser ? (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate animate-pulse text-slate-500">
                  Loading profile...
                </p>
              </div>
            ) : currentUser ? (
              <>
                <img
                  src={
                    currentUser.avatar ??
                    'https://ui-avatars.com/api/?name=User&background=random'
                  }
                  alt={currentUser.fullName}
                  className="w-8 h-8 rounded-full border border-slate-600"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{currentUser.fullName}</p>
                  <p className="text-xs text-slate-400 truncate capitalize">
                    {userRole.replace('_', ' ').toLowerCase()}
                  </p>
                </div>
              </>
            ) : (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-red-400">User Not Found</p>
              </div>
            )}
          </div>
          {onSignOut && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSignOut();
              }}
              className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition"
            >
              <LogOut size={16} />
              Sign out
            </button>
          )}
        </div>
            </div>
          </>
        )}
      </div>
    </aside>
  );
};

